import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { loginUserAPI, signUpUserAPI, setAuthToken } from "../../../services/authService";

/**
 * Sign up user with email/password or Google auth
 * @param {Object} payload - {email, password?, signup_type: 'email'|'google', auth_token?}
 */
export const signUpUser = createAsyncThunk(
  "auth/register",
  async (payload, { rejectWithValue }) => {
    try {
      const data = await signUpUserAPI(payload);
      // store token if present
      const token = data?.data?.token || data?.token || data?.result?.authToken || null;
      if (token) {
        try {
          localStorage.setItem("token", token);
          setAuthToken(token);
        } catch (err) {
          console.warn("Could not persist token to localStorage", err);
        }
      }
      return data;
    } catch (error) {
      console.log("Signup error:", error);
      return rejectWithValue(error?.response?.data || error);
    }
  }
);

/**
 * Login user with email/password or Google auth
 * @param {Object} payload - {email, password?, signup_type: 'email'|'google', auth_token?}
 */
export const loginUser = createAsyncThunk(
  "auth/login",
  async (payload, { rejectWithValue }) => {
    try {
      const data = await loginUserAPI(payload);
      console.log("login response", data);
      // store token if present
      const token = data?.data?.token || data?.token || data?.result?.authToken || null;
      if (token) {
        try {
          localStorage.setItem("token", token);
          setAuthToken(token);
        } catch (err) {
          console.warn("Could not persist token to localStorage", err);
        }
      }
      return data;
    } catch (error) {
      console.log("Login error:", error);
      return rejectWithValue(error?.response?.data || error);
    }
  }
);

export const authSlice = createSlice({
  name: "auth",
  initialState: {
    token: typeof window !== 'undefined' ? (localStorage.getItem('token') || null) : null,
    user: typeof window !== 'undefined' ? (() => {
      try {
        const u = localStorage.getItem('user');
        return u ? JSON.parse(u) : null;
      } catch (e) {
        return null;
      }
    })() : null,
    remember_me: false,
    email: null,
    password: null,
    isLoading: false,
    error: null,
  },

  reducers: {
    logOutUser: (state) => {
      state.token = null;
      state.user = null;
      state.error = null;
      try {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('subscription');
        setAuthToken(null);
      } catch (err) {
        // ignore
      }
    },

    updateUser: (state, action) => {
      // merge provided fields into the user object
      state.user = Object.assign({}, state.user || {}, action.payload || {});
      if (state.user) {
        try {
          localStorage.setItem('user', JSON.stringify(state.user));
        } catch (e) {}
      }
    },

    rememberMe: (state, action) => {
      state.remember_me = action.payload.remember_me;
      state.email = action.payload.email;
      state.password = action.payload.password;
    },

    clearError: (state) => {
      state.error = null;
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(signUpUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(signUpUser.fulfilled, (state, action) => {
        state.token = action.payload?.data?.token || action.payload?.token || action.payload?.result?.authToken || null;
        state.user = action.payload?.data?.user || action.payload?.user || action.payload?.result?.user || null;
        if (state.user && state.user.email) state.email = state.user.email;
        state.isLoading = false;
        state.error = null;
        if (state.user) {
          try {
            localStorage.setItem('user', JSON.stringify(state.user));
          } catch (e) {}
        }
      })
      .addCase(signUpUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(loginUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.token = action.payload?.data?.token || action.payload?.token || action.payload?.result?.authToken || null;
        state.user = action.payload?.data?.user || action.payload?.user || action.payload?.result?.user || null;
        if (state.user && state.user.email) state.email = state.user.email;
        state.isLoading = false;
        state.error = null;
        if (state.user) {
          try {
            localStorage.setItem('user', JSON.stringify(state.user));
          } catch (e) {}
        }
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const { logOutUser, updateUser, rememberMe, clearError } = authSlice.actions;

export default authSlice.reducer;
